import { defineEvent, withPii } from "@amtana/module";
import { z } from "zod";

import { membershipRoleSchema } from "./organization.schemas.ts";

// Organization's facts (ADR-022, epic 26, S26.03/S26.13): one stream per invitation,
// `organization:<invitationId>`, tenanted by the organization it belongs to.
// A fact carries ids, addresses and roles a person may see, and never a token
// or a link (ADR-022 point 7): the invite's own link is minted fresh, from the
// invitation row, only once a `transactionalEmail` delivery is about to mail it
// (`OrganizationApi.mintInviteLink`, peer-only, reached through worker-bridge the
// same way auth's own links are, D500/D509).
//
// `organizationName` travels on every fact that plans an email (the router cannot
// peer organization for a live read without a boot cycle, revision 1); `invitedByUserId`
// travels wherever the delivery needs to resolve the inviter's own name and language at
// send time. `held` is true only on a `member_invited` fact raised past the seat cap
// (S3.07): the router plans no delivery for one until `invite_released` says it is time.
// `invitedUserId` is set when the invited address already has an account (D510): that
// person also hears a settings-aware "invite-received" notice beside the transactional
// invitation email.
//
// `organizationName` is marked `.meta({ piiFree: true })` (review group 3 security finding
// 11, `apps/api/src/__tests__/pii-registry.unit.test.ts`): an organization's own name, never a
// person's, the only field here the repo-wide PII heuristic's own `name$` regex would
// otherwise flag — declaring it explicitly, on the field itself, replaced a name-based
// exemption list the walker used to keep of its own.

export const ORGANIZATION = "organization";

// `invitedByUserId`/`invitedUserId` are declared subject id fields (review group 3 security
// finding 5, `withPii(def, {pii, subjects})`): the inviter and, once admitted, the invited
// person are each found by their own erasure through this fact even when nothing left in
// `piiPaths` (the invited address alone) names the one being erased — the admin who sent an
// invite is a different person than the one it was sent to.

export const memberInvited = withPii(
  defineEvent(
    ORGANIZATION,
    "member_invited",
    z.object({
      invitationId: z.string(),
      organizationId: z.string(),
      organizationName: z.string().meta({ piiFree: true }),
      email: z.string(),
      role: membershipRoleSchema,
      invitedByUserId: z.string(),
      held: z.boolean(),
      invitedUserId: z.string().nullable(),
    }),
  ),
  { pii: ["email"], subjects: ["invitedByUserId", "invitedUserId"] },
);

export const inviteResent = withPii(
  defineEvent(
    ORGANIZATION,
    "invite_resent",
    z.object({
      invitationId: z.string(),
      organizationId: z.string(),
      organizationName: z.string().meta({ piiFree: true }),
      email: z.string(),
      role: membershipRoleSchema,
      invitedByUserId: z.string(),
    }),
  ),
  { pii: ["email"], subjects: ["invitedByUserId"] },
);

export const inviteAccepted = withPii(
  defineEvent(
    ORGANIZATION,
    "invite_accepted",
    z.object({
      invitationId: z.string(),
      organizationId: z.string(),
      organizationName: z.string().meta({ piiFree: true }),
      userId: z.string(),
      role: membershipRoleSchema,
      invitedByUserId: z.string(),
    }),
  ),
  { pii: [], subjects: ["invitedByUserId"] },
);

/** Let out of hold once a seat frees or the cap grows (S3.07); released.ts's own fact. */
export const inviteReleased = withPii(
  defineEvent(
    ORGANIZATION,
    "invite_released",
    z.object({
      invitationId: z.string(),
      organizationId: z.string(),
      organizationName: z.string().meta({ piiFree: true }),
      email: z.string(),
      role: membershipRoleSchema,
      invitedByUserId: z.string(),
      invitedUserId: z.string().nullable(),
    }),
  ),
  { pii: ["email"], subjects: ["invitedByUserId", "invitedUserId"] },
);

export const organizationEvents = [memberInvited, inviteResent, inviteAccepted, inviteReleased] as const;
